import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import AdminLogos from "./AdminLogos";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ToggleRow({ label, desc, on, onToggle, testid }) {
  return (
    <div className="flex items-start justify-between gap-6 py-5 border-t border-[#27272A] first:border-t-0 first:pt-0 last:pb-0">
      <div>
        <p className="text-[10px] uppercase tracking-widest text-[#71717A]">{label}</p>
        <p className="text-sm text-[#A1A1AA] mt-1.5 leading-relaxed">{desc}</p>
      </div>
      <button
        type="button"
        onClick={onToggle}
        data-testid={testid}
        aria-pressed={on}
        className={`shrink-0 w-14 h-8 border transition-colors duration-300 relative ${on ? "bg-[#C5A059] border-[#C5A059]" : "bg-[#0A0A0C] border-[#3f3f46]"}`}
      >
        <span className={`absolute top-1 w-5 h-5 transition-all duration-300 ${on ? "right-1 bg-black" : "left-1 bg-[#71717A]"}`} />
      </button>
    </div>
  );
}

export default function AdminSettings() {
  const [adresse, setAdresse] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [detailsVisibles, setDetailsVisibles] = useState(false);
  const [programmeVisible, setProgrammeVisible] = useState(true);
  const [equipesVisibles, setEquipesVisibles] = useState(true);
  const [progCats, setProgCats] = useState({ u19m: true, u23m: true, u19f: true, u23f: true });
  const [logosVis, setLogosVis] = useState({ sponsors: false, clubs: false, partenaires: false });
  const [partenaires, setPartenaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/settings`).then((r) => {
      setAdresse(r.data.bureau_adresse);
      setWhatsapp(r.data.whatsapp || "");
      setDetailsVisibles(!!r.data.details_visibles);
      setProgrammeVisible(r.data.programme_visible !== false);
      setEquipesVisibles(r.data.equipes_visibles !== false);
      if (r.data.programme_categories) setProgCats((p) => ({ ...p, ...r.data.programme_categories }));
      setLogosVis({
        sponsors: !!r.data.logos_sponsors_visibles,
        clubs: !!r.data.logos_clubs_visibles,
        partenaires: !!r.data.logos_partenaires_visibles,
      });
      setPartenaires(r.data.partenaires || []);
      setLoading(false);
    }).catch(() => toast.error("Erreur de chargement"));
  }, []);
  useEffect(load, [load]);

  if (loading) return <p className="text-[#71717A] text-sm">Chargement…</p>;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, {
        bureau_adresse: adresse,
        partenaires,
        details_visibles: detailsVisibles,
        logos_sponsors_visibles: logosVis.sponsors,
        logos_clubs_visibles: logosVis.clubs,
        logos_partenaires_visibles: logosVis.partenaires,
        programme_visible: programmeVisible,
        equipes_visibles: equipesVisibles,
        programme_categories: progCats,
        whatsapp: whatsapp || "+243862624007",
      }, { withCredentials: true });
      toast.success("Réglages enregistrés");
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const toggleLogo = (key) => setLogosVis((v) => ({ ...v, [key]: !v[key] }));

  return (
    <form onSubmit={save} data-testid="admin-settings-form" className="max-w-4xl">
      <div className="space-y-10">
        <div className="border border-[#27272A] bg-[#121214] p-6">
          <ToggleRow
            label="Détails de l'événement sur la page publique"
            desc={detailsVisibles
              ? "Les détails (programme, lieu, phases…) sont VISIBLES sur les pages Événement et Détection."
              : "Les détails sont FLOUTÉS avec le badge « Coming Soon » — seules les dates sont visibles."}
            on={detailsVisibles}
            onToggle={() => setDetailsVisibles(!detailsVisibles)}
            testid="settings-toggle-details"
          />
          <p className="text-[11px] text-[#71717A] mt-4">Activez pour retirer le flou « Coming Soon » quand tout est prêt. Pensez à enregistrer.</p>
        </div>
        <div className="border border-[#27272A] bg-[#121214] p-6">
          <ToggleRow
            label="Programme officiel du tournoi (pages Détection & Événement)"
            desc={programmeVisible
              ? "Le programme officiel (catégories, calendrier des matchs, classements) est VISIBLE sur le site."
              : "Le programme officiel est FLOUTÉ avec le badge « Coming Soon »."}
            on={programmeVisible}
            onToggle={() => setProgrammeVisible(!programmeVisible)}
            testid="settings-toggle-programme"
          />
          <div className="mt-2 border-t border-[#27272A] pt-2">
            <p className="text-[10px] uppercase tracking-widest text-[#C5A059] py-3">Publication du programme par catégorie — publiez chaque catégorie quand elle est complète</p>
            {[["u19m", "U19 Masculin"], ["u23m", "U23 Masculin"], ["u19f", "U19 Féminin"], ["u23f", "U23 Féminin"]].map(([k, label]) => (
              <ToggleRow
                key={k}
                label={`Programme ${label}`}
                desc={progCats[k] ? `Les matchs ${label} sont visibles dans le programme.` : `Les matchs ${label} sont masqués du programme public.`}
                on={!!progCats[k]}
                onToggle={() => setProgCats((p) => ({ ...p, [k]: !p[k] }))}
                testid={`settings-toggle-progcat-${k}`}
              />
            ))}
          </div>
          <p className="text-[11px] text-[#71717A] mt-4">Désactivez pour masquer temporairement tout le programme officiel, ou masquez catégorie par catégorie. Pensez à enregistrer.</p>
        </div>
        <div className="border border-[#27272A] bg-[#121214] p-6">
          <ToggleRow
            label="Équipes & informations des équipes (pages Détection & fiches équipe)"
            desc={equipesVisibles
              ? "Les équipes publiées sont VISIBLES sur le site (liste + pages d'équipe)."
              : "La section Équipes est FLOUTÉE « Coming Soon » et les fiches équipe sont masquées."}
            on={equipesVisibles}
            onToggle={() => setEquipesVisibles(!equipesVisibles)}
            testid="settings-toggle-equipes"
          />
          <p className="text-[11px] text-[#71717A] mt-4">Masquez temporairement toutes les informations des équipes (la publication équipe par équipe reste gérée dans l'onglet Équipes). Pensez à enregistrer.</p>
        </div>
        <div className="border border-[#27272A] bg-[#121214] p-6">
          <p className="text-[10px] uppercase tracking-widest text-[#C5A059] mb-2">Flou « Coming Soon » des logos — par catégorie (page Événement)</p>
          <ToggleRow
            label="Sponsors officiels"
            desc={logosVis.sponsors ? "Les logos des SPONSORS sont visibles." : "Les logos des sponsors sont floutés."}
            on={logosVis.sponsors}
            onToggle={() => toggleLogo("sponsors")}
            testid="settings-toggle-logos-sponsors"
          />
          <ToggleRow
            label="Clubs recruteurs"
            desc={logosVis.clubs ? "Les logos des CLUBS RECRUTEURS sont visibles." : "Les logos des clubs recruteurs sont floutés."}
            on={logosVis.clubs}
            onToggle={() => toggleLogo("clubs")}
            testid="settings-toggle-logos-clubs"
          />
          <ToggleRow
            label="Partenaires"
            desc={logosVis.partenaires ? "Les logos des PARTENAIRES sont visibles." : "Les logos des partenaires sont floutés."}
            on={logosVis.partenaires}
            onToggle={() => toggleLogo("partenaires")}
            testid="settings-toggle-logos-partenaires"
          />
          <p className="text-[11px] text-[#71717A] mt-4">Chaque catégorie est indépendante : affichez les sponsors sans dévoiler les clubs, par exemple. Pensez à enregistrer.</p>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Numéro WhatsApp de contact (bouton WhatsApp de la page Détection — format international, ex : +243862624007)</label>
          <input data-testid="settings-whatsapp" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} className="input-editorial mt-1 mb-6" placeholder="+243862624007" />
          <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Adresse exacte du bureau (affichée aux joueurs + emails RDV)</label>
          <input data-testid="settings-bureau-adresse" required value={adresse} onChange={(e) => setAdresse(e.target.value)} className="input-editorial mt-1" placeholder="Ex : 12, Av. de la Libération, Gombe — Kinshasa" />
        </div>
        <AdminLogos />
      </div>
      <button type="submit" disabled={saving} data-testid="settings-save-button" className="mt-10 inline-flex items-center gap-3 bg-[#C5A059] text-black px-8 py-3.5 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#E5C07B] transition-colors duration-300 disabled:opacity-60">
        {saving && <Loader2 size={15} className="animate-spin" />}
        Enregistrer les réglages
      </button>
    </form>
  );
}
